/**
 * Deterministic, offline IEEE reference-list linter.
 *
 * The same contract as the APA, MLA, Chicago and Harvard linters
 * (`src/lib/citation-check*.ts`): pure string in, findings out, high precision, every
 * finding advisory — a likely issue to review, never a verdict. It does not import the
 * citation core, so it cannot move a golden snapshot. Unlike those four, the words of
 * each finding live in the widget's strings (keyed by `rule`) so a translated page can
 * say them; a finding carries only the values its message interpolates.
 *
 * Rules, verified against the IEEE Reference Guide (IEEE Publication Operations,
 * V 3.28.2025, linked from the IEEE Author Center) unless noted:
 * - "Reference numbers are set flush left … enclosed in square brackets." References
 *   are numbered in the order they are first cited; "There must be only one reference
 *   with each number."
 * - "The given name of the author or editor is abbreviated to the initial only and
 *   precedes the last name. Do not use commas around Jr., Sr., and III in names."
 * - "IEEE publications must list names of all authors, up to six names. If there are
 *   more than six names listed, use the primary author's name followed by 'et al.'"
 * - Two authors are joined with "and"; three or more take a comma before the final
 *   "and" — every example in the guide ("T. Lippert, and A. Wokaun").
 * - Periodicals: J. K. Author, "Name of paper," Abbrev. Title of Periodical, vol. x,
 *   no. x, pp. xxx–xxx, Abbrev. Month, year, doi: xxx. A single page is "p. 475".
 * - Months are abbreviated with a period — Jan., Feb., Mar., Apr., May, Jun., Jul.,
 *   Aug., Sep., Oct., Nov., Dec. — in every example in the 2025 guide.
 * - "All references, except those ending with URLs, will end with a period, including
 *   those with a DOI."
 * - IEEE Editorial Style Manual for Authors (IEEE Author Center): "Punctuation always
 *   goes inside quotation marks, except for the colon and semicolon"; the same source
 *   listed more than once "should all be made into one reference."
 */

import {
  alphabeticalFinding,
  type CheckReport,
  duplicateFindings,
  type EntryReport,
  endsWithTerminal,
  type Finding,
  finishReport,
  fullRange,
  type ListEntry,
  type ListRule,
  numberingFindings,
  parseNumberedList,
} from "@/lib/tool-kit/reference-checkers/numbered-list";

export type IeeeRule =
  | ListRule
  | "bracket-numbers"
  | "author-inverted"
  | "author-initials-after"
  | "author-suffix-comma"
  | "author-ampersand"
  | "author-and-missing"
  | "author-serial-comma"
  | "author-two-comma"
  | "et-al-threshold"
  | "et-al-first-only"
  | "title-quotes-missing"
  | "title-single-quotes"
  | "title-punctuation"
  | "locator-style"
  | "volume-format"
  | "issue-format"
  | "pages-format"
  | "month-format"
  | "doi-format"
  | "terminal-period";

export type IeeeSource = "guide" | "manual";

/** Where each rule is written down. The link labels live in the widget's strings. */
export const IEEE_SOURCES: Record<IeeeSource, string> = {
  guide:
    "https://journals.ieeeauthorcenter.ieee.org/wp-content/uploads/sites/7/IEEE_Reference_Guide.pdf",
  manual:
    "https://journals.ieeeauthorcenter.ieee.org/your-role-in-article-production/ieee-editorial-style-manual/",
};

export type IeeeFinding = Finding<IeeeRule, IeeeSource>;
export type IeeeReport = CheckReport<IeeeRule, IeeeSource>;

// --- authors -----------------------------------------------------------------------

type IeeeNameForm = "natural" | "inverted" | "vancouver";

interface IeeeName {
  raw: string;
  form: IeeeNameForm;
  surname: string;
  /** "J. K." — initials as IEEE writes them. */
  initials: string;
  /** "Jr." / "III" — written without a comma before it. */
  suffix: string;
  /** The writer put a comma before the suffix: "J. Smith, Jr." */
  suffixComma: boolean;
}

const PARTICLE =
  "(?:(?:van|von|de|der|den|da|di|du|la|le|del|della|dos|das|ter|ten|bin|al|el)\\s)*";
const SURNAME_WORD = "\\p{Lu}[\\p{Ll}'’][\\p{L}'’-]*";
const SURNAME = `${PARTICLE}${SURNAME_WORD}(?:[\\s-]${SURNAME_WORD})?`;
const STOP = '(?=[,.;:“"]|\\s+(?:and|&)\\s|\\s+et\\.?\\s?al|\\s*$)';

const RE_NATURAL = new RegExp(
  `((?:\\p{Lu}\\p{Ll}?\\.\\s?-?\\s?){1,4})(${SURNAME})(?:(,?)\\s(Jr\\.|Sr\\.|II|III|IV)(?![\\p{L}]))?${STOP}`,
  "uy"
);
/** "Smith, J. K." — the APA/Harvard order, before a comma, "and", or the quoted title. */
const RE_INVERTED = new RegExp(
  `(${SURNAME}),\\s((?:\\p{Lu}\\.\\s?-?){1,3})(?=\\s*[,;“"]|\\s+(?:and|&)\\s|\\s*$)`,
  "uy"
);
/** "Smith JK" — but not "Molignaro III", where the capitals are a generational suffix. */
const RE_VANCOUVER = new RegExp(
  `(${SURNAME})\\s(?!(?:II|III|IV)\\b)(\\p{Lu}{1,3})(?=[,.;]|\\s+(?:and|&)\\s)`,
  "uy"
);
const RE_ETAL = /,?\s*et\.?\s?al\b\.?/y;
const RE_SEPARATOR = /(,\s*and\s+|,\s*&\s*|\s+and\s+|\s*&\s*|,\s*)/y;
const RE_AUTHOR_END = /^\s*(?:,|[“"]|\.|:|$)/;
const RE_NOT_CAPITAL = /[^\p{Lu}]/gu;
const RE_TRAILING_SPACE = /\s+$/;

function at(re: RegExp, text: string, pos: number): RegExpExecArray | null {
  re.lastIndex = pos;
  return re.exec(text);
}

function dotted(letters: string): string {
  return letters
    .replace(RE_NOT_CAPITAL, "")
    .split("")
    .map((letter) => `${letter}.`)
    .join(" ");
}

function matchIeeeName(text: string, pos: number): IeeeName | null {
  const natural = at(RE_NATURAL, text, pos);
  if (natural) {
    return {
      raw: natural[0].replace(RE_TRAILING_SPACE, ""),
      form: "natural",
      surname: natural[2],
      initials: natural[1].trim(),
      suffix: natural[4] ?? "",
      suffixComma: natural[3] === ",",
    };
  }
  const inverted = at(RE_INVERTED, text, pos);
  if (inverted) {
    return {
      raw: inverted[0].replace(RE_TRAILING_SPACE, ""),
      form: "inverted",
      surname: inverted[1],
      initials: dotted(inverted[2]),
      suffix: "",
      suffixComma: false,
    };
  }
  const vancouver = at(RE_VANCOUVER, text, pos);
  if (vancouver) {
    return {
      raw: vancouver[0],
      form: "vancouver",
      surname: vancouver[1],
      initials: dotted(vancouver[2]),
      suffix: "",
      suffixComma: false,
    };
  }
  return null;
}

type Separator = "comma" | "and" | "comma-and" | "amp";

interface IeeeAuthors {
  names: IeeeName[];
  separators: Separator[];
  etAl: boolean;
}

function separatorKind(raw: string): Separator {
  if (raw.includes("&")) {
    return "amp";
  }
  if (raw.includes("and")) {
    return raw.includes(",") ? "comma-and" : "and";
  }
  return "comma";
}

/**
 * The names before the title. Null when the entry does not open with a readable list
 * of personal names — an organization, a website, a standard — so nothing is said.
 */
function parseIeeeAuthors(text: string): IeeeAuthors | null {
  const names: IeeeName[] = [];
  const separators: Separator[] = [];
  let pos = 0;
  let etAl = false;
  for (let guard = 0; guard < 100; guard++) {
    const name = matchIeeeName(text, pos);
    if (!name) {
      break;
    }
    names.push(name);
    pos += name.raw.length;
    const etAlMatch = at(RE_ETAL, text, pos);
    if (etAlMatch) {
      etAl = true;
      pos += etAlMatch[0].length;
      break;
    }
    const separator = at(RE_SEPARATOR, text, pos);
    const next = separator ? pos + separator[0].length : pos;
    if (!(separator && matchIeeeName(text, next))) {
      break;
    }
    separators.push(separatorKind(separator[0]));
    pos = next;
  }
  if (names.length === 0 || !RE_AUTHOR_END.test(text.slice(pos))) {
    return null;
  }
  return { names, separators, etAl };
}

function ieeeName(name: IeeeName): string {
  const base = `${name.initials} ${name.surname}`;
  return name.suffix ? `${base} ${name.suffix}` : base;
}

/** The author element as IEEE writes it. */
function ieeeAuthorList(names: readonly IeeeName[], etAl: boolean): string {
  const shown = names.map(ieeeName);
  if (etAl || shown.length > 6) {
    return `${shown[0]} et al.`;
  }
  if (shown.length <= 2) {
    return shown.join(" and ");
  }
  return `${shown.slice(0, -1).join(", ")}, and ${shown.at(-1)}`;
}

function finding(
  rule: IeeeRule,
  confidence: "high" | "medium",
  suggestion?: string,
  values?: Record<string, string | number>
): IeeeFinding {
  return { rule, confidence, source: "guide", suggestion, values };
}

/** Commas and "and" between natural-order names. */
function separatorFindings(authors: IeeeAuthors, fixed: string): IeeeFinding[] {
  const { names, separators, etAl } = authors;
  if (etAl || separators.includes("amp")) {
    return [];
  }
  const last = separators.at(-1);
  if (names.length >= 3 && last === "comma") {
    return [finding("author-and-missing", "medium", fixed)];
  }
  if (names.length >= 3 && last === "and") {
    return [finding("author-serial-comma", "medium", fixed)];
  }
  if (names.length === 2 && last === "comma-and") {
    return [finding("author-two-comma", "medium", fixed)];
  }
  return [];
}

function etAlFindings(authors: IeeeAuthors, fixed: string): IeeeFinding[] {
  const { names, etAl } = authors;
  if (!etAl && names.length > 6) {
    return [finding("et-al-threshold", "high", fixed, { count: names.length })];
  }
  if (etAl && names.length > 1) {
    return [finding("et-al-first-only", "medium", fixed)];
  }
  return [];
}

function authorFindings(authors: IeeeAuthors): IeeeFinding[] {
  const { names, separators } = authors;
  const fixed = ieeeAuthorList(names, authors.etAl);
  const forms = new Set(names.map((name) => name.form));
  const findings: IeeeFinding[] = [];
  // A list in another style's name form gets one finding whose suggestion is the whole
  // corrected element — commas, "and" and et al. included — rather than one per symptom.
  if (forms.has("inverted") || forms.has("vancouver")) {
    const rule = forms.has("inverted")
      ? "author-inverted"
      : "author-initials-after";
    findings.push(finding(rule, "high", fixed));
    if (!authors.etAl && names.length > 6) {
      findings.push(
        finding("et-al-threshold", "high", fixed, { count: names.length })
      );
    }
    return findings;
  }
  const suffixComma = names.some((name) => name.suffixComma);
  if (suffixComma) {
    findings.push(finding("author-suffix-comma", "high", fixed));
  }
  if (separators.includes("amp")) {
    findings.push(finding("author-ampersand", "high", fixed));
  }
  findings.push(...etAlFindings(authors, fixed));
  // "J. Smith, Jr., and A. Doe": the comma before "and" belongs to the suffix, and the
  // suffix finding's suggestion already removes both.
  if (!suffixComma) {
    findings.push(...separatorFindings(authors, fixed));
  }
  return findings;
}

// --- title -------------------------------------------------------------------------

const RE_DOUBLE_QUOTED = /([“"])([^“”"]{2,})([”"])/;
const RE_SINGLE_QUOTED = /‘([^’]{3,})’/;
const RE_TITLE_HAS_STOP = /[,.?!]$/;
const RE_PERIODICAL = /\bvol\.?\s*\d/i;
const RE_PERIODICAL_PARTS = /\b(?:no|pp)\.?\s*\d/i;
const RE_PROCEEDINGS = /\bin\s+Proc\./;
/** The guide's own "Special Issue on …, Proc. IEEE, vol. 84, …" has no quoted title. */
const RE_SPECIAL_ISSUE = /^Special\s+(?:Issue|Section)\b/i;

interface TitleSpan {
  start: number;
  end: number;
}

function quotedTitleFindings(
  text: string,
  quoted: RegExpExecArray
): { findings: IeeeFinding[]; span: TitleSpan } {
  const span = { start: quoted.index, end: quoted.index + quoted[0].length };
  const [, open, body, close] = quoted;
  const inner = body.trim();
  const after = text[span.end];
  const findings: IeeeFinding[] = [];
  if ((after === "," || after === ".") && !RE_TITLE_HAS_STOP.test(inner)) {
    findings.push({
      rule: "title-punctuation",
      confidence: "high",
      source: "manual",
      values: { found: `${close}${after}`, expected: `${after}${close}` },
      suggestion: `${open}${inner}${after}${close}`,
    });
  }
  return { findings, span };
}

function titleFindings(
  text: string,
  looksLikeArticle: boolean
): { findings: IeeeFinding[]; span: TitleSpan | null } {
  const quoted = RE_DOUBLE_QUOTED.exec(text);
  if (quoted) {
    return quotedTitleFindings(text, quoted);
  }
  const single = RE_SINGLE_QUOTED.exec(text);
  if (single) {
    return {
      findings: [
        finding(
          "title-single-quotes",
          "medium",
          `“${single[1].replace(RE_TITLE_HAS_STOP, "")},”`
        ),
      ],
      span: { start: single.index, end: single.index + single[0].length },
    };
  }
  const periodical =
    looksLikeArticle ||
    (RE_PERIODICAL.test(text) && RE_PERIODICAL_PARTS.test(text)) ||
    RE_PROCEEDINGS.test(text);
  const missing = periodical && !RE_SPECIAL_ISSUE.test(text);
  return {
    findings: missing ? [finding("title-quotes-missing", "medium")] : [],
    span: null,
  };
}

/** The entry with its title blanked out, so a word in the title is never mistaken for a locator. */
function maskTitle(text: string, span: TitleSpan | null): string {
  if (!span) {
    return text;
  }
  return (
    text.slice(0, span.start) +
    " ".repeat(span.end - span.start) +
    text.slice(span.end)
  );
}

// --- locators ------------------------------------------------------------------------

/** "2020;12(3):45-60" — the Vancouver/AMA locator block, in an IEEE list. */
const RE_BIOMEDICAL_LOCATOR =
  /\b((?:19|20)\d{2})(?:\s+[A-Z][a-z]{2}\.?(?:\s+\d{1,2})?)?\s*;\s*(\d+)\s*(?:\(([^)]+)\))?\s*:\s*([A-Za-z]?\d+)(?:\s*[-–]\s*([A-Za-z]?\d+))?/;
const RE_VOLUME = /\b(?:Vol\.?|vol(?![.\p{L}])|[Vv]olume|vol\.(?=\d))\s*(\d+)/u;
const RE_ISSUE =
  /,\s(?:No\.?|no(?![.\p{L}])|[Ii]ssue|[Ii]ss\.|no\.(?=\d))\s*(\d+)/u;
const RE_P_RANGE = /\bp\.\s?([A-Z]?\d+)\s?[-–—]\s?([A-Z]?\d+)/;
const RE_PP_SINGLE = /\bpp\.\s?(\d+)(?!\d)(?!\s*[-–—,&]|\s+and\b)/;
const RE_PAGES_WORD = /\b(?:pages?|pgs?\.?)\s+(\d+)(?:\s?[-–—]\s?(\d+))?/i;
const RE_BARE_RANGE = /,\s(\d{1,6})\s?[-–—]\s?(\d{1,6})(?=[,.]|$)/;
const RE_HAS_PAGES = /\bpp?\.\s?[A-Z]?\d/;

function fix(
  rule: IeeeRule,
  found: string,
  expected: string,
  confidence: "high" | "medium"
): IeeeFinding {
  return {
    rule,
    confidence,
    source: "guide",
    values: { found: found.trim(), expected },
    suggestion: expected,
  };
}

/** The same volume, issue, pages and year, written the IEEE way. */
function ieeeLocator(match: RegExpExecArray): string {
  const [, year, volume, issue, first, last] = match;
  const parts = [`vol. ${volume}`];
  if (issue) {
    parts.push(`no. ${issue}`);
  }
  if (last) {
    const full = fullRange(first, last)?.split("-")[1] ?? last;
    parts.push(`pp. ${first}–${full}`);
  } else {
    parts.push(`p. ${first}`);
  }
  parts.push(year);
  return parts.join(", ");
}

function locatorStyleFinding(text: string): IeeeFinding | null {
  const match = RE_BIOMEDICAL_LOCATOR.exec(text);
  return match
    ? fix("locator-style", match[0], ieeeLocator(match), "high")
    : null;
}

function volumeIssueFindings(masked: string): IeeeFinding[] {
  const findings: IeeeFinding[] = [];
  const volume = RE_VOLUME.exec(masked);
  if (volume) {
    findings.push(
      fix("volume-format", volume[0], `vol. ${volume[1]}`, "medium")
    );
  }
  const issue = RE_ISSUE.exec(masked);
  if (issue) {
    findings.push(
      fix("issue-format", issue[0].slice(1), `no. ${issue[1]}`, "medium")
    );
  }
  return findings;
}

function isYearRange(first: string, last: string): boolean {
  const a = Number(first);
  const b = Number(last);
  return (
    first.length === 4 && last.length === 4 && a >= 1500 && b <= 2100 && b > a
  );
}

function pagesFinding(masked: string): IeeeFinding | null {
  const pRange = RE_P_RANGE.exec(masked);
  if (pRange) {
    return fix(
      "pages-format",
      pRange[0],
      `pp. ${pRange[1]}–${pRange[2]}`,
      "high"
    );
  }
  const single = RE_PP_SINGLE.exec(masked);
  if (single) {
    return fix("pages-format", single[0], `p. ${single[1]}`, "medium");
  }
  const word = RE_PAGES_WORD.exec(masked);
  if (word) {
    const expected = word[2] ? `pp. ${word[1]}–${word[2]}` : `p. ${word[1]}`;
    return fix("pages-format", word[0], expected, "medium");
  }
  const bare = RE_BARE_RANGE.exec(masked);
  if (bare && !RE_HAS_PAGES.test(masked) && !isYearRange(bare[1], bare[2])) {
    return fix(
      "pages-format",
      bare[0].slice(1),
      `pp. ${bare[1]}–${bare[2]}`,
      "high"
    );
  }
  return null;
}

const DATE_AFTER = "(?=\\s+(?:\\d{1,2}(?:[–-]\\d{1,2})?,\\s+)?\\d{4})";
const RE_MONTH_FULL = new RegExp(
  `\\b(January|February|March|April|June|July|August|September|October|November|December)\\b${DATE_AFTER}`
);
const RE_MONTH_BARE = new RegExp(
  `\\b(Jan|Feb|Mar|Apr|Jun|Jul|Aug|Sep|Sept|Oct|Nov|Dec)\\b(?!\\.)${DATE_AFTER}`
);
const RE_SEPT = /\bSept\.(?=\s)/;

/** IEEE's month abbreviations, from the Reference Guide's examples. */
const IEEE_MONTHS: Record<string, string> = {
  jan: "Jan.",
  feb: "Feb.",
  mar: "Mar.",
  apr: "Apr.",
  jun: "Jun.",
  jul: "Jul.",
  aug: "Aug.",
  sep: "Sep.",
  oct: "Oct.",
  nov: "Nov.",
  dec: "Dec.",
};

function monthFinding(masked: string): IeeeFinding | null {
  const match =
    RE_MONTH_FULL.exec(masked) ??
    RE_MONTH_BARE.exec(masked) ??
    RE_SEPT.exec(masked);
  if (!match) {
    return null;
  }
  const expected = IEEE_MONTHS[match[0].slice(0, 3).toLowerCase()];
  return expected ? fix("month-format", match[0], expected, "medium") : null;
}

const RE_DOI_PREFIX = /\b(doi\s*:\s*)(10\.\d{4,9}\/\S+?)(?=[.,;]?(?:\s|$))/i;
const RE_DOI_URL =
  /(?<!Available:\s?)\bhttps?:\/\/(?:dx\.)?doi\.org\/(10\.\d{4,9}\/\S+?)(?=[.,;]?(?:\s|$))/i;
const RE_ONLY_PERIOD = /^\.?$/;

interface DoiCheck {
  finding: IeeeFinding | null;
  /** The DOI is the last thing in the entry, so its fix carries the final period. */
  atEnd: boolean;
}

function doiCheck(text: string): DoiCheck {
  const trimmed = text.trim();
  const prefixed = RE_DOI_PREFIX.exec(trimmed);
  const url = prefixed ? null : RE_DOI_URL.exec(trimmed);
  const match = prefixed ?? url;
  if (!match) {
    return { finding: null, atEnd: false };
  }
  if (prefixed && prefixed[1] === "doi: ") {
    return { finding: null, atEnd: false };
  }
  const doi = prefixed ? prefixed[2] : (url as RegExpExecArray)[1];
  const atEnd = RE_ONLY_PERIOD.test(
    trimmed.slice(match.index + match[0].length)
  );
  const expected = `doi: ${doi}${atEnd ? "." : ""}`;
  return { finding: fix("doi-format", match[0], expected, "medium"), atEnd };
}

const RE_ENDS_WITH_URL = /https?:\/\/\S+$/i;

function periodFinding(text: string): IeeeFinding | null {
  const trimmed = text.trim();
  if (RE_ENDS_WITH_URL.test(trimmed) && !RE_DOI_URL.test(trimmed)) {
    return null;
  }
  if (endsWithTerminal(trimmed)) {
    return null;
  }
  return finding("terminal-period", "high");
}

// --- the check -----------------------------------------------------------------------

function pushAll(
  target: IeeeFinding[],
  items: readonly (IeeeFinding | null)[]
): void {
  for (const item of items) {
    if (item) {
      target.push(item);
    }
  }
}

function checkEntry(entry: ListEntry): {
  findings: IeeeFinding[];
  surname: string | null;
} {
  const findings: IeeeFinding[] = [];
  const authors = parseIeeeAuthors(entry.text);
  if (authors) {
    findings.push(...authorFindings(authors));
  }
  const locator = locatorStyleFinding(entry.text);
  const title = titleFindings(entry.text, locator !== null);
  findings.push(...title.findings);
  const masked = maskTitle(entry.text, title.span);
  if (locator) {
    findings.push(locator);
  } else {
    findings.push(...volumeIssueFindings(masked));
    pushAll(findings, [pagesFinding(masked)]);
  }
  const doi = doiCheck(entry.text);
  pushAll(findings, [
    monthFinding(masked),
    doi.finding,
    doi.atEnd ? null : periodFinding(entry.text),
  ]);
  return { findings, surname: authors?.names[0]?.surname ?? null };
}

function bracketFinding(entries: readonly ListEntry[]): IeeeFinding | null {
  const other = entries.find(
    (entry) => entry.marker !== null && entry.marker !== "bracket"
  );
  if (!other || other.label === null) {
    return null;
  }
  return {
    rule: "bracket-numbers",
    confidence: "medium",
    source: "guide",
    values: { example: `[${other.label}]` },
  };
}

/** Check a pasted IEEE reference list. Pure — no network, no mutation. */
export function checkIeeeReferences(text: string): IeeeReport {
  const list = parseNumberedList(text);
  const surnames: (string | null)[] = [];
  const entries: EntryReport<IeeeRule, IeeeSource>[] = list.map((entry) => {
    const result = checkEntry(entry);
    surnames.push(result.surname);
    return {
      position: entry.position,
      label: entry.label,
      text: entry.text,
      findings: result.findings,
    };
  });
  const listFindings: IeeeFinding[] = [];
  pushAll(listFindings, [bracketFinding(list)]);
  listFindings.push(...numberingFindings(list, "guide"));
  listFindings.push(...duplicateFindings(list, "manual"));
  pushAll(listFindings, [alphabeticalFinding(surnames, "guide")]);
  return finishReport(entries, listFindings);
}
